/**
 * @file useProfessionals.js
 *
 * Professional Hooks
 *
 * React hooks that manage professional data for UI components.
 * Hooks are the bridge between API services and page components.
 *
 * Pattern:
 *   Page component → useHook() → service → API → backend → database
 *
 * Three hooks for three use cases:
 *   useProfessionals()       — list all professionals (for browsing)
 *   useProfessionalProfile() — current user's own profile (for editing)
 *   useProfessionalDetail()  — view one specific professional (by ID)
 *
 * Key React concepts used:
 *   useState    — holds data that triggers re-renders when it changes
 *   useEffect   — runs code when component mounts or dependencies change
 *   useCallback — memoizes a function so it doesn't get recreated every render
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { professionalService } from "../services";
import { PROFESSIONAL_STRINGS } from "../constants/strings";

/**
 * Hook to fetch and hold the list of all professionals.
 *
 * @param {Object} [options]
 * @param {boolean} [options.autoFetch=true] - Whether to fetch on mount
 * @returns {{professionals: Array, isLoading: boolean, error: Error|null, refresh: Function}}
 */
export function useProfessionals(options = {}) {
  const { autoFetch = true } = options; // autoFetch=false lets you delay the API call
  const [professionals, setProfessionals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // useCallback prevents this function from being recreated on every render,
  // which would cause useEffect to re-run infinitely
  const fetchProfessionals = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await professionalService.getAll();
      setProfessionals(response.data || []); // response.data comes from the API interceptor
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false); // Always stop loading, even if there was an error
    }
  }, []);

  // useEffect with [autoFetch, fetchProfessionals] runs once on mount
  useEffect(() => {
    if (autoFetch) fetchProfessionals();
  }, [autoFetch, fetchProfessionals]);

  // Return state + a refresh function the component can call to re-fetch
  return { professionals, isLoading, error, refresh: fetchProfessionals };
}

/**
 * Hook for the logged-in professional to view and edit their own profile.
 *
 * @param {boolean} [enabled=true] - Set to false to skip fetching (e.g., for non-professional users)
 * @returns {{profile: Object|null, isLoading: boolean, error: Error|null, updateProfile: Function, updateProfileSilent: Function, refresh: Function}}
 */
export function useProfessionalProfile(enabled = true) {
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState(null);

  const fetchProfile = useCallback(async () => {
    if (!enabled) return;
    try {
      setIsLoading(true);
      setError(null);
      const response = await professionalService.getMe();
      setProfile(response.data);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [enabled]);

  // Update profile and immediately reflect changes in local state
  const updateProfile = async (data) => {
    const response = await professionalService.updateMe(data);
    setProfile(response.data); // Update local state with server response
    toast.success(PROFESSIONAL_STRINGS.SAVED); // Show success notification
    return response.data;
  };

  // Silent update — saves to server without showing the success toast
  const updateProfileSilent = async (data) => {
    const response = await professionalService.updateMe(data);
    setProfile(response.data);
    return response.data;
  };

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  return { profile, isLoading, error, updateProfile, updateProfileSilent, refresh: fetchProfile };
}

/**
 * Hook to fetch a single professional's details by ID (for the public profile page).
 *
 * @param {string|number} id - Professional ID
 * @returns {{professional: Object|null, isLoading: boolean, error: Error|null, refresh: Function}}
 */
export function useProfessionalDetail(id) {
  const [professional, setProfessional] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProfessional = useCallback(async () => {
    if (!id) return; // Don't fetch if no ID (component might not have it yet)
    try {
      setIsLoading(true);
      setError(null);
      const response = await professionalService.getById(id);
      setProfessional(response.data);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [id]); // Re-fetch when ID changes (e.g., navigating between profiles)

  useEffect(() => {
    fetchProfessional();
  }, [fetchProfessional]);

  return { professional, isLoading, error, refresh: fetchProfessional };
}
